import { useState, type ReactNode } from 'react';
import { Button } from 'equasion-lib';
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiChevronRight,
  FiPrinter,
  FiRotateCcw
} from 'react-icons/fi';
import { documentActionLabel } from '@/lib/rxPrint';
import { packDocumentsReady } from '../../verifyScreen';
import { eqProps } from '../../eqProps';

export type PackDocument = {
  id?: string | null;
  filename?: string | null;
  humanFilename?: string | null;
  link?: string | null;
} | null;

export type PackDocumentsOrder = {
  id: string;
  vendorSalesOrderNumber?: string | null;
  vendorStatus?: string | null;
  packInvoiceDocument?: PackDocument;
  packPackingListDocument?: PackDocument;
  packStickersDocument?: PackDocument;
} | null;

export type PackDocumentsPanelProps = {
  open: boolean;
  order: PackDocumentsOrder;
  /** SO number to show before the docs query has answered. */
  fallbackOrderNumber?: string | null;
  heading: string;
  message: ReactNode;
  /** Still waiting for a document inside the poll window. */
  polling: boolean;
  /** The poll window ran out with a document still missing: offer Check again + Regenerate. */
  timedOut: boolean;
  regenerating?: boolean;
  /**
   * Print All's one merged PDF is being made on the server. Regenerate and Reopen Verify wait for
   * it: either would replace or void the documents it is merging.
   */
  printingAll?: boolean;
  /**
   * RxPrint on: Print sends a document to its mapped printer and Print All sends every box
   * document; off: the buttons read "Download…" and download (see @/lib/rxPrint).
   */
  rxPrintEnabled?: boolean;
  /** The id of the document being sent to RxPrint, or 'all' for Print All. */
  sendingKey?: string | null;
  onPrint: (document: NonNullable<PackDocument>, fallbackFilename: string) => void;
  onPrintAll: () => void;
  onCheckAgain: () => void;
  onRegenerate: () => void;
  onContinueToShip: () => void;
  onDone: () => void;
  /** Offered for a verified order waiting to ship: back to Verify (confirmed first). */
  onReopenVerify?: (() => void) | null;
  reopening?: boolean;
  /**
   * The other orders of this order's COMBINED shipment (combined at the end of Pick or at Verify):
   * they go in the same box, each with its own documents. Each opens its own documents panel.
   */
  combinedOrders?: { id: string; vendorSalesOrderNumber?: string | null }[];
  onOpenCombinedOrder?: ((orderId: string) => void) | null;
};

/**
 * The "print the documents for the box" prompt after Verify (design §7.3, D14): the invoice and
 * packing list printed at Verify (vendor-only rows, the box's final paperwork), each "Generating…"
 * until its signed link exists, then printable: sent to its RxPrint printer when the vendor has
 * RxPrint on, downloaded otherwise (and labelled "Download…"). Print All waits for both and, without
 * RxPrint, downloads them as one PDF, packing list first; the single buttons stay as the fallback.
 * Presentational: PackDocumentsPanel.data owns the query, the 2 s × 90 s poll, and the Regenerate,
 * Print All and RxPrint mutations.
 *
 * Rendered as the app's `.result-panel` (the full-screen "you're done — what next" surface), so the
 * Verify list stays mounted underneath while the camera does not (the page unmounts the scanner
 * whenever this is open).
 */
function PackDocumentsPanel({
  open,
  order,
  fallbackOrderNumber,
  heading,
  message,
  polling,
  timedOut,
  regenerating = false,
  printingAll = false,
  rxPrintEnabled = false,
  sendingKey = null,
  onPrint,
  onPrintAll,
  onCheckAgain,
  onRegenerate,
  onContinueToShip,
  onDone,
  onReopenVerify,
  reopening = false,
  combinedOrders = [],
  onOpenCombinedOrder = null
}: PackDocumentsPanelProps) {
  const [confirmReopen, setConfirmReopen] = useState(false);
  const ready = packDocumentsReady(order);
  const orderNumber = order?.vendorSalesOrderNumber || fallbackOrderNumber || '';

  const printButton = (
    document: PackDocument,
    label: string,
    fallbackFilename: string,
    isReady: boolean
  ) => (
    <Button
      type="secondary"
      fullWidth
      className="verify-docs__print document-print-button"
      disabled={!isReady || !!sendingKey}
      iconBefore={<FiPrinter />}
      {...eqProps({
        onClick: () => document?.link && !sendingKey && onPrint(document, fallbackFilename)
      })}
    >
      {!isReady
        ? `Generating ${label}…`
        : sendingKey && sendingKey === document?.id
        ? 'Sending…'
        : documentActionLabel(rxPrintEnabled, label)}
    </Button>
  );

  return (
    <div
      className={['result-panel', 'verify-docs', open && 'open']
        .filter(Boolean)
        .join(' ')}
      aria-hidden={!open}
    >
      {open && (
        <>
          <div className="result-message verify-docs__message">
            <FiCheckCircle style={{ color: 'var(--green)' }} />
            <h1>{heading}</h1>
            <p>{message}</p>
            {!!orderNumber && <p className="verify-docs__order">SO #{orderNumber}</p>}
          </div>

          {combinedOrders.length > 0 && (
            <div className="verify-docs__combined" role="note">
              <p>
                Same box — each order has its own documents. Print theirs too:
              </p>
              <div className="verify-docs__combined-orders">
                {combinedOrders.map((combined) => (
                  <button
                    key={combined.id}
                    type="button"
                    className="verify-docs__combined-order"
                    disabled={!onOpenCombinedOrder}
                    onClick={() => onOpenCombinedOrder?.(combined.id)}
                  >
                    SO #{combined.vendorSalesOrderNumber} <FiChevronRight aria-hidden />
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="verify-docs__prints">
            <Button
              type="primary"
              fullWidth
              className="verify-docs__print-all document-print-button"
              disabled={!ready.all || printingAll || reopening}
              iconBefore={<FiPrinter />}
              {...eqProps({
                onClick: () => ready.all && !printingAll && !reopening && onPrintAll()
              })}
            >
              {!ready.all
                ? 'Generating documents…'
                : printingAll
                ? rxPrintEnabled
                  ? 'Sending…'
                  : 'Preparing…'
                : documentActionLabel(rxPrintEnabled, 'All Documents')}
            </Button>
            {printButton(
              order?.packPackingListDocument ?? null,
              'Packing List',
              `Packing List - SO ${orderNumber || 'order'}.pdf`,
              ready.packingList
            )}
            {printButton(
              order?.packInvoiceDocument ?? null,
              'Invoice',
              `Invoice - SO ${orderNumber || 'order'}.pdf`,
              ready.invoice
            )}
            {printButton(
              order?.packStickersDocument ?? null,
              'Stickers',
              order?.packStickersDocument?.humanFilename ||
                `stickers-${orderNumber || 'order'}.zpl`,
              !!order?.packStickersDocument?.link
            )}
            {polling && !ready.all && (
              <p className="verify-docs__note" role="status">
                The documents are being generated — this usually takes a few seconds.
              </p>
            )}
            {timedOut && !ready.all && (
              <div className="verify-docs__recovery" role="alert">
                <p>
                  <FiAlertTriangle /> The documents are taking longer than usual. Check
                  again, or regenerate them if they still do not appear.
                </p>
                <div className="verify-docs__recovery-actions">
                  <Button
                    type="secondary"
                    fullWidth
                    {...eqProps({ onClick: onCheckAgain })}
                  >
                    Check again
                  </Button>
                  <Button
                    type="secondary"
                    fullWidth
                    disabled={regenerating || printingAll}
                    {...eqProps({ onClick: onRegenerate })}
                  >
                    {regenerating ? 'Regenerating…' : 'Regenerate'}
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className="result-actions verify-docs__actions">
            <Button type="primary" fullWidth {...eqProps({ onClick: onContinueToShip })}>
              Continue to Ship
            </Button>
            <Button type="secondary" fullWidth {...eqProps({ onClick: onDone })}>
              Done
            </Button>
            {!!onReopenVerify && !confirmReopen && (
              <button
                type="button"
                className="verify-docs__reopen"
                disabled={reopening || printingAll}
                onClick={() => setConfirmReopen(true)}
              >
                <FiRotateCcw aria-hidden /> Reopen Verify
              </button>
            )}
            {!!onReopenVerify && confirmReopen && (
              <div className="verify-docs__reopen-confirm" role="alertdialog">
                <p>
                  Reopen Verify? The order goes back to the Verify step so its units can
                  be changed. These documents are voided; new ones print when Verify
                  completes again.
                </p>
                <div className="verify-docs__recovery-actions">
                  <Button
                    type="danger"
                    fullWidth
                    disabled={reopening || printingAll}
                    {...eqProps({
                      onClick: () => {
                        setConfirmReopen(false);
                        onReopenVerify();
                      }
                    })}
                  >
                    {reopening ? 'Reopening…' : 'Reopen Verify'}
                  </Button>
                  <Button
                    type="secondary"
                    fullWidth
                    {...eqProps({ onClick: () => setConfirmReopen(false) })}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default PackDocumentsPanel;
